import { useEffect, useRef, useState, type FormEvent } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Public prelaunch landing — notify-first, plain-language pitch.
 * Console remains at /app for founders with an unlock.
 */

const NAV = [
  { id: "why-outpost", label: "Why" },
  { id: "preview", label: "Preview" },
  { id: "how-it-works", label: "How" },
  { id: "building", label: "Building" },
  { id: "pilot", label: "Pilot" },
  { id: "faq", label: "FAQ" },
  { id: "notify", label: "Notify" },
] as const;

const FEATURED_ON = [
  {
    name: "Fazier",
    url: "https://fazier.com",
    logo: "https://www.google.com/s2/favicons?domain=fazier.com&sz=64",
  },
  {
    name: "Twelve Tools",
    url: "https://twelve.tools/outpost16",
    logo: "https://www.google.com/s2/favicons?domain=twelve.tools&sz=64",
  },
  {
    name: "Firsto",
    url: "https://firsto.co",
    logo: "https://www.google.com/s2/favicons?domain=firsto.co&sz=64",
  },
  {
    name: "Findly.tools",
    url: "https://findly.tools",
    logo: "https://findly.tools/badges/findly-tools-badge-light.svg",
  },
  {
    name: "Super Launch",
    url: "https://super-launch.app",
    logo: "https://super-launch.app/badge/badge_light.svg",
  },
  {
    name: "Aura++",
    url: "https://auraplusplus.com/projects/outpost16",
    logo: "https://auraplusplus.com/images/badges/featured-on-light.svg",
  },
  {
    name: "Launch Llama",
    url: "https://tools.launchllama.co/products/outpost16?utm_source=badge&utm_medium=referral",
    logo: "https://tools.launchllama.co/featured-badge.png?v=2",
  },
  {
    name: "NoonLaunch",
    url: "https://noonlaunch.com/product/outpost16",
    logo: "https://noonlaunch.com/badges/outpost16.svg",
  },
  {
    name: "LaunchVault",
    url: "https://www.launchvault.dev",
    logo: "https://www.launchvault.dev/images/badges/launch-valut-badge.svg",
  },
  {
    name: "Dofollow.Tools",
    url: "https://dofollow.tools",
    logo: "https://dofollow.tools/favicon.ico",
  },
] as const;

const FAQ: Array<{ q: string; a: string }> = [
  {
    q: "What is Outpost16?",
    a: "One place for founders with several products to see what’s changing in growth, and choose what to work on next — without living in six analytics tabs.",
  },
  {
    q: "Who is it for?",
    a: "Solo entrepreneurs juggling more than one product or idea at once — people who need a calm portfolio view instead of six analytics tabs.",
  },
  {
    q: "What is the feedback pilot?",
    a: "We’re opening five slots for founders who want early access and will share candid feedback. It’s not a launch waitlist — it’s a small working group while we shape Outpost16.",
  },
  {
    q: "How is this different from Google Analytics or DataFast?",
    a: "Those tools are great for deep analysis. Outpost answers a simpler question: is the signal moving, and what should I do next? Detailed charts stay in the tools you already use.",
  },
  {
    q: "Which tools does it connect to?",
    a: "We’re wiring product analytics, search/site traffic, SEO authority, and social publishing first (for example PostHog, Google, Ahrefs, Buffer). You keep owning those accounts — Outpost reads the signals.",
  },
  {
    q: "Can I use my own AI agents?",
    a: "Yes. Outpost is meant to be the system of record for your GTM state. Your agents can connect later; we don’t host or bill model usage.",
  },
  {
    q: "When will it launch?",
    a: "When the lookout is useful enough to invite more founders. Leave your email on the launch list and you’ll get an update when that happens — plus occasional development notes before then.",
  },
  {
    q: "What do you do with my email?",
    a: "Launch-list emails are only for Outpost16 updates. Pilot applications are only used to contact you about the feedback program. No selling lists. Unsubscribe anytime. See Privacy below.",
  },
];

type SubStatus = "idle" | "loading" | "ok" | "already" | "error";
type PilotStatus = SubStatus | "full";

type PilotSlotInfo = {
  slotsTotal: number;
  slotsTaken: number;
  slotsLeft: number;
  open: boolean;
};

function NotifyForm({
  idPrefix,
  email,
  setEmail,
  status,
  message,
  onSubmit,
  dark,
}: {
  idPrefix: string;
  email: string;
  setEmail: (v: string) => void;
  status: SubStatus;
  message: string;
  onSubmit: (e: FormEvent, source: string) => void;
  dark?: boolean;
}) {
  const source = idPrefix === "hero" ? "landing-hero" : "landing-bottom";
  return (
    <div className={dark ? "op-teaser-notify op-teaser-notify-dark" : "op-teaser-notify"}>
      <form
        className="op-teaser-subscribe"
        onSubmit={(e) => onSubmit(e, source)}
      >
        <label className="op-sr-only" htmlFor={`${idPrefix}-email`}>
          Email address
        </label>
        <input
          id={`${idPrefix}-email`}
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="Your email address"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          disabled={status === "loading"}
        />
        <button
          type="submit"
          className="op-primary"
          disabled={status === "loading"}
        >
          {status === "loading" ? "Saving…" : "Notify me at launch"}
        </button>
      </form>
      <p className="op-teaser-notify-note">
        Occasional product updates, plus an email when we launch. Unsubscribe
        anytime.{" "}
        <a href="#privacy">Privacy</a>
      </p>
      {message ? (
        <p
          className={
            status === "error"
              ? "op-teaser-sub-msg op-teaser-sub-msg-err"
              : "op-teaser-sub-msg"
          }
          role="status"
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}

export function Teaser() {
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState<SubStatus>("idle");
  const [subMessage, setSubMessage] = useState("");
  const [pilotEmail, setPilotEmail] = useState("");
  const [pilotUrls, setPilotUrls] = useState<string[]>([""]);
  const [pilotNote, setPilotNote] = useState("");
  const [pilotStatus, setPilotStatus] = useState<PilotStatus>("idle");
  const [pilotMessage, setPilotMessage] = useState("");
  const [slots, setSlots] = useState<PilotSlotInfo | null>(null);
  const [overHero, setOverHero] = useState(true);
  const stageRef = useRef<HTMLElement>(null);

  useEffect(() => {
    document.title = "Outpost16 — Keep growth in sight";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/v1/public/pilot");
        const data = (await res.json().catch(() => ({}))) as Partial<PilotSlotInfo> & {
          ok?: boolean;
        };
        if (cancelled || !res.ok || !data.ok) return;
        setSlots({
          slotsTotal: data.slotsTotal ?? 5,
          slotsTaken: data.slotsTaken ?? 0,
          slotsLeft: data.slotsLeft ?? 5,
          open: data.open ?? true,
        });
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setOverHero(
          Boolean(entry?.isIntersecting && (entry.intersectionRatio ?? 0) > 0.4),
        );
      },
      { threshold: [0, 0.25, 0.4, 0.6, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  function resetStatusOnType(next: string) {
    setEmail(next);
    if (subStatus !== "idle" && subStatus !== "loading") {
      setSubStatus("idle");
      setSubMessage("");
    }
  }

  function clearPilotMsg() {
    if (pilotStatus !== "idle" && pilotStatus !== "loading") {
      setPilotStatus("idle");
      setPilotMessage("");
    }
  }

  async function onSubscribe(e: FormEvent, source: string) {
    e.preventDefault();
    if (subStatus === "loading") return;
    setSubStatus("loading");
    setSubMessage("");
    try {
      const res = await fetch("/v1/public/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        alreadySubscribed?: boolean;
        message?: string;
      };
      if (!res.ok || !data.ok) {
        setSubStatus("error");
        setSubMessage(data.message || "Could not save your email. Try again.");
        return;
      }
      if (data.alreadySubscribed) {
        setSubStatus("already");
        setSubMessage("You’re already on the list — we’ll email you at launch.");
      } else {
        setSubStatus("ok");
        setSubMessage("You’re on the list. We’ll email you when Outpost16 launches.");
        setEmail("");
      }
    } catch {
      setSubStatus("error");
      setSubMessage("Network error. Try again in a moment.");
    }
  }

  async function onPilotApply(e: FormEvent) {
    e.preventDefault();
    if (pilotStatus === "loading") return;
    if (slots && !slots.open) {
      setPilotStatus("full");
      setPilotMessage("All pilot slots are filled.");
      return;
    }
    setPilotStatus("loading");
    setPilotMessage("");
    try {
      const res = await fetch("/v1/public/pilot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: pilotEmail,
          productUrls: pilotUrls,
          note: pilotNote,
          source: "landing-pilot",
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        alreadyApplied?: boolean;
        message?: string;
        slotsTotal?: number;
        slotsTaken?: number;
        slotsLeft?: number;
        open?: boolean;
      };
      if (
        typeof data.slotsTotal === "number" &&
        typeof data.slotsTaken === "number" &&
        typeof data.slotsLeft === "number"
      ) {
        setSlots({
          slotsTotal: data.slotsTotal,
          slotsTaken: data.slotsTaken,
          slotsLeft: data.slotsLeft,
          open: data.open ?? data.slotsLeft > 0,
        });
      }
      if (res.status === 409 || data.open === false) {
        setPilotStatus("full");
        setPilotMessage(data.message || "All pilot slots are filled.");
        return;
      }
      if (!res.ok || !data.ok) {
        setPilotStatus("error");
        setPilotMessage(data.message || "Could not submit. Try again.");
        return;
      }
      if (data.alreadyApplied) {
        setPilotStatus("already");
        setPilotMessage("You’re already on the pilot list — we’ll be in touch.");
      } else {
        setPilotStatus("ok");
        setPilotMessage(
          "Thanks — you’re in the pilot queue. We’ll email you shortly.",
        );
        setPilotEmail("");
        setPilotUrls([""]);
        setPilotNote("");
      }
    } catch {
      setPilotStatus("error");
      setPilotMessage("Network error. Try again in a moment.");
    }
  }

  const pilotOpen = slots?.open !== false;
  const slotsLabel =
    slots == null
      ? "5 feedback slots"
      : slots.open
        ? `${slots.slotsLeft} of ${slots.slotsTotal} slots left`
        : "All 5 slots filled";

  return (
    <div className="op-teaser">
      <header
        className={`op-teaser-top${overHero ? " is-over-hero" : " is-over-body"}`}
      >
        <a className="op-teaser-mark" href="#top" aria-label="Outpost16 home">
          <img
            src="/brand/outpost16-lookout-app-mark.png"
            alt=""
            width={32}
            height={32}
          />
        </a>
        <nav className="op-teaser-nav" aria-label="On this page">
          {NAV.map((item) => (
            <a key={item.id} href={`#${item.id}`}>
              {item.label}
            </a>
          ))}
        </nav>
        <a className="op-teaser-login" href="/app">
          Founder login
        </a>
      </header>

      <section
        ref={stageRef}
        id="top"
        className="op-teaser-stage"
        aria-label="Hero"
      >
        <div className="op-teaser-sky" aria-hidden>
          <img
            className="op-teaser-hero"
            src="/brand/outpost16-hero-background.png"
            alt=""
          />
          <div className="op-teaser-embers" />
          <div className="op-teaser-veil" />
        </div>

        <div className="op-teaser-hero-copy">
          <p className="op-teaser-eyebrow">
            In development · For founders building multiple products
          </p>
          <p className="op-teaser-brand">
            outpost<span className="op-brand-number">16</span>
          </p>
          <p className="op-teaser-tagline">Keep growth in sight.</p>
          <h1>Know what’s growing. Know what to do next.</h1>
          <p className="op-teaser-sub">
            Outpost16 brings growth signals from your products and marketing
            tools into one place, so you can see what’s changing and choose
            your next action.
          </p>
          <NotifyForm
            idPrefix="hero"
            email={email}
            setEmail={resetStatusOnType}
            status={subStatus}
            message={subMessage}
            onSubmit={onSubscribe}
            dark
          />
          <p className="op-teaser-hero-pilot-cue">
            Looking for early access?{" "}
            <a href="#pilot">{slotsLabel}</a>
          </p>
        </div>

        <a
          className="op-teaser-scroll-cue"
          href="#why-outpost"
          aria-label="Scroll down to learn more"
        >
          <span className="op-teaser-scroll-cue-label">See more</span>
          <span className="op-teaser-scroll-cue-arrow" aria-hidden>
            <ChevronDown size={36} strokeWidth={2.75} />
          </span>
        </a>
      </section>

      <div className="op-teaser-body">
        <section className="op-teaser-section" id="why-outpost">
          <p className="op-teaser-kicker">Why Outpost</p>
          <h2>See the change. Choose the next move.</h2>
          <p>
            Analytics tools tell you what happened in detail. Outpost is for
            the decision in front of you: which product needs attention, whether
            a growth signal is actually moving, and what to try next — then
            record whether that action helped. Deep analysis stays in the tools
            you already use.
          </p>
        </section>

        <section className="op-teaser-section" id="preview">
          <p className="op-teaser-kicker">Product preview</p>
          <h2>One look at the lookout.</h2>
          <p className="op-teaser-lead">
            Example: traffic is growing, but signups are flat. Review the signup
            journey, make a change, and track the result.
          </p>
          <figure className="op-teaser-preview">
            <figcaption className="op-teaser-preview-label">
              Preview · illustrative · functionality we are building
            </figcaption>
            <div className="op-teaser-preview-frame">
              <div className="op-teaser-preview-bar">
                <span className="op-teaser-preview-dot" />
                <span className="op-teaser-preview-dot" />
                <span className="op-teaser-preview-dot" />
                <span className="op-teaser-preview-title">Growth</span>
              </div>
              <div className="op-teaser-preview-products" aria-label="Products">
                <span className="op-teaser-chip is-active">All products</span>
                <span className="op-teaser-chip">AI Asset Vault</span>
                <span className="op-teaser-chip">Klinky</span>
                <span className="op-teaser-chip">PixelWatch</span>
              </div>
              <div className="op-teaser-preview-chapter">
                <div className="op-teaser-preview-chapter-head">
                  <div>
                    <strong>Activation</strong>
                    <small>Product analytics · last 7 days</small>
                  </div>
                  <span className="op-teaser-preview-tag">Did signups move?</span>
                </div>
                <div className="op-teaser-preview-hero-row">
                  <div>
                    <div className="op-teaser-preview-num">12</div>
                    <div className="op-teaser-preview-num-label">
                      Signups 7d · flat vs prior week
                    </div>
                  </div>
                  <div className="op-teaser-preview-spark" aria-hidden>
                    <svg viewBox="0 0 120 40" width="120" height="40">
                      <path
                        d="M0 28 L20 24 L40 26 L60 18 L80 22 L100 14 L120 16"
                        fill="none"
                        stroke="#D8754F"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                </div>
                <div className="op-teaser-preview-supports">
                  <div>
                    <small>Site sessions 7d</small>
                    <strong>+18%</strong>
                  </div>
                  <div>
                    <small>First value 7d</small>
                    <strong>4</strong>
                  </div>
                </div>
              </div>
              <div className="op-teaser-preview-action">
                <strong>Suggested next</strong>
                <p>
                  Review the signup journey on AI Asset Vault — traffic is up,
                  signups are not.
                </p>
                <div className="op-teaser-preview-action-row">
                  <span className="op-teaser-preview-btn">Take this action</span>
                  <span className="op-teaser-preview-btn-ghost">
                    Record result later
                  </span>
                </div>
              </div>
            </div>
          </figure>
        </section>

        <section className="op-teaser-section" id="how-it-works">
          <p className="op-teaser-kicker">How it works</p>
          <h2>Connect. Watch. Act.</h2>
          <div className="op-teaser-steps">
            <article className="op-teaser-step">
              <span
                className="op-teaser-strip-cell op-teaser-steps-cell-0"
                aria-hidden
              />
              <h3>Connect your tools</h3>
              <p>
                Link the analytics and marketing tools you already use. Missing
                data stays blank — we never invent zeros.
              </p>
            </article>
            <article className="op-teaser-step">
              <span
                className="op-teaser-strip-cell op-teaser-steps-cell-1"
                aria-hidden
              />
              <h3>Watch what moved</h3>
              <p>
                See signups, traffic, authority, and publishing at a glance —
                one clear question per source.
              </p>
            </article>
            <article className="op-teaser-step">
              <span
                className="op-teaser-strip-cell op-teaser-steps-cell-2"
                aria-hidden
              />
              <h3>Act, then check</h3>
              <p>
                Pick a concrete next action, do the work, and record whether it
                helped — so the next hour compounds.
              </p>
            </article>
          </div>
        </section>

        <section className="op-teaser-section" id="building">
          <p className="op-teaser-kicker">What we’re building</p>
          <h2>Benefits first. Plumbing second.</h2>
          <div className="op-teaser-trail" aria-hidden>
            <span className="op-teaser-strip-cell op-teaser-trail-cell-0" />
            <span className="op-teaser-strip-cell op-teaser-trail-cell-1" />
            <span className="op-teaser-strip-cell op-teaser-trail-cell-2" />
          </div>
          <ul className="op-teaser-cook-list">
            <li>
              <strong>Portfolio clarity</strong> — switch between products and
              see which ones need attention this week.
            </li>
            <li>
              <strong>Honest growth signals</strong> — activation, traffic, SEO
              authority, and publishing without drowning in dashboards.
            </li>
            <li>
              <strong>Next actions with follow-through</strong> — suggested work
              you can accept, complete, and mark as helpful or not.
            </li>
            <li>
              <strong>Your stack, your agents</strong> — connect existing tools;
              bring your own agents later. We don’t sell model spend.
            </li>
          </ul>
        </section>

        <section className="op-teaser-section op-teaser-pilot" id="pilot">
          <p className="op-teaser-kicker">Feedback pilot</p>
          <h2>Five slots for solo founders juggling more than one thing.</h2>
          <p className="op-teaser-lead">
            We’re looking for solo entrepreneurs with several ideas or products
            in flight — people who need one calm place to see what’s moving and
            what to do next. Apply for a feedback pilot slot: early access in
            exchange for candid notes on what helps and what doesn’t.
          </p>
          <p className="op-teaser-pilot-slots" aria-live="polite">
            <strong>{slotsLabel}</strong>
            {slots == null || slots.open
              ? " · Not a launch waitlist — a small working group."
              : " · Join the launch list below to hear when more seats open."}
          </p>

          {pilotOpen ? (
            <form className="op-teaser-pilot-form" onSubmit={onPilotApply}>
              <label className="op-teaser-field">
                <span>Email</span>
                <input
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={pilotEmail}
                  disabled={pilotStatus === "loading"}
                  onChange={(ev) => {
                    setPilotEmail(ev.target.value);
                    clearPilotMsg();
                  }}
                />
              </label>

              <fieldset className="op-teaser-field">
                <legend>Product URLs (optional)</legend>
                <p className="op-teaser-field-hint">
                  One or more — whatever you’re shipping or thinking about.
                </p>
                {pilotUrls.map((url, idx) => (
                  <div key={idx} className="op-teaser-url-row">
                    <input
                      type="text"
                      name={`productUrl-${idx}`}
                      inputMode="url"
                      placeholder="https://yourproduct.com"
                      value={url}
                      disabled={pilotStatus === "loading"}
                      onChange={(ev) => {
                        const next = [...pilotUrls];
                        next[idx] = ev.target.value;
                        setPilotUrls(next);
                        clearPilotMsg();
                      }}
                    />
                    {pilotUrls.length > 1 ? (
                      <button
                        type="button"
                        className="op-linkish"
                        disabled={pilotStatus === "loading"}
                        onClick={() => {
                          setPilotUrls(pilotUrls.filter((_, i) => i !== idx));
                          clearPilotMsg();
                        }}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                ))}
                {pilotUrls.length < 5 ? (
                  <button
                    type="button"
                    className="op-linkish op-teaser-add-url"
                    disabled={pilotStatus === "loading"}
                    onClick={() => {
                      setPilotUrls([...pilotUrls, ""]);
                      clearPilotMsg();
                    }}
                  >
                    Add another product
                  </button>
                ) : null}
              </fieldset>

              <label className="op-teaser-field">
                <span>Short note (optional)</span>
                <textarea
                  name="note"
                  rows={3}
                  maxLength={1000}
                  placeholder="What are you building, and why does a multi-product lookout matter to you?"
                  value={pilotNote}
                  disabled={pilotStatus === "loading"}
                  onChange={(ev) => {
                    setPilotNote(ev.target.value);
                    clearPilotMsg();
                  }}
                />
              </label>

              <button
                type="submit"
                className="op-primary"
                disabled={pilotStatus === "loading"}
              >
                {pilotStatus === "loading"
                  ? "Sending…"
                  : "Request a pilot slot"}
              </button>
              <p className="op-teaser-notify-note">
                We’ll only use this to contact you about the feedback pilot.{" "}
                <a href="#privacy">Privacy</a>
              </p>
              {pilotMessage ? (
                <p
                  className={
                    pilotStatus === "error" || pilotStatus === "full"
                      ? "op-teaser-sub-msg op-teaser-sub-msg-err"
                      : "op-teaser-sub-msg"
                  }
                  role="status"
                >
                  {pilotMessage}
                </p>
              ) : null}
            </form>
          ) : (
            <div className="op-teaser-pilot-closed">
              <p>
                All five pilot slots are spoken for. Thanks for the interest —
                join the launch list below and we’ll shout when we open more.
              </p>
              <a className="op-primary" href="#notify">
                Join the launch list
              </a>
            </div>
          )}
        </section>

        <section className="op-teaser-section" id="faq">
          <p className="op-teaser-kicker">Questions</p>
          <h2>FAQ</h2>
          <div className="op-teaser-faq">
            {FAQ.map((item) => (
              <details key={item.q} className="op-teaser-faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="op-teaser-section op-teaser-signal" id="notify">
          <div className="op-teaser-signal-art" aria-hidden>
            <img
              src="/brand/outpost16-landing-signal-fire.png"
              alt=""
              width={128}
              height={128}
            />
          </div>
          <p className="op-teaser-kicker">Launch list</p>
          <h2>Be first to hear when Outpost16 launches.</h2>
          <p>
            Get occasional development updates and an email when Outpost16 is
            available.
          </p>
          <NotifyForm
            idPrefix="bottom"
            email={email}
            setEmail={resetStatusOnType}
            status={subStatus}
            message={subMessage}
            onSubmit={onSubscribe}
          />
        </section>

        <section className="op-teaser-section" id="privacy">
          <p className="op-teaser-kicker">Privacy</p>
          <h2>How we use your email</h2>
          <p>
            If you join the launch list, we store your email to send Outpost16
            product updates and a launch notice. If you apply for the feedback
            pilot, we store your email, optional product URLs, and note only to
            contact you about that program. We don’t sell or share the list for
            advertising. You can unsubscribe from any email. Questions:{" "}
            <a href="mailto:robin@wilgott.com">robin@wilgott.com</a>.
          </p>
        </section>

        <section className="op-teaser-section op-teaser-featured" id="featured-on">
          <p className="op-teaser-kicker">Featured On</p>
          <h2>Outpost16.com is joining the founder directory trail.</h2>
          <p className="op-teaser-lead">
            Explore the directories where we’re building an honest, public
            presence as Outpost16 develops.
          </p>
          <div className="op-teaser-featured-grid" aria-label="Featured on directories">
            {FEATURED_ON.map((item) =>
              item.name === "Super Launch" ? (
                <a key={item.name} href={item.url} target="_blank">
                  <img
                    src={item.logo}
                    alt="Featured on Super Launch"
                    width={200}
                    height={54}
                  />
                </a>
              ) : (
                <a
                  key={item.name}
                  className="op-teaser-featured-card"
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={item.logo} alt="" width={32} height={32} loading="lazy" />
                  <span>{item.name}</span>
                </a>
              ),
            )}
          </div>
        </section>
      </div>

      <footer className="op-teaser-site-foot">
        <a href="https://findly.tools" target="_blank" rel="noopener noreferrer">
          <img
            src="https://findly.tools/badges/findly-tools-badge-light.svg"
            alt="Featured on Findly.tools"
            width={175}
            height={55}
          />
        </a>
        <span>In development · Keep growth in sight</span>
        <span>outpost16.com</span>
        <a href="/app">Founder login</a>
      </footer>
    </div>
  );
}
